import { useEffect, useState } from 'react';

interface ScrollDirectionOptions {
  /** At or above the top of the container (within this many px), the
   *  direction resets to 'up' — the resting state shows everything. */
  topThreshold?: number;
  /** Ignore scroll jitter smaller than this many px (trackpad noise,
   *  momentum-scroll bounce). */
  minDelta?: number;
}

/**
 * Tracks which way a scroll container is moving: 'down' while the user
 * scrolls away from the top, 'up' the moment they reverse (and always 'up'
 * at rest near the top). Drives hide-on-scroll-down / reveal-on-scroll-up
 * chrome — the assets page's sub-lg control row.
 *
 * Takes the element (not a ref) so callers can hand it a callback-ref state
 * value; conditionally rendered scroll containers (behind a loading gate)
 * then re-arm the listener automatically when they mount.
 */
export function useScrollDirection(
  element: HTMLElement | null,
  { topThreshold = 8, minDelta = 4 }: ScrollDirectionOptions = {}
): 'up' | 'down' {
  const [direction, setDirection] = useState<'up' | 'down'>('up');

  useEffect(() => {
    if (!element) return;
    let lastTop = element.scrollTop;
    const onScroll = () => {
      const top = element.scrollTop;
      // Rubber-band overscroll reports negative values; treat as top.
      if (top <= topThreshold) {
        setDirection('up');
        lastTop = Math.max(0, top);
        return;
      }
      const delta = top - lastTop;
      if (Math.abs(delta) < minDelta) return;
      setDirection(delta > 0 ? 'down' : 'up');
      lastTop = top;
    };
    element.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      element.removeEventListener('scroll', onScroll);
      // A remounting container starts a fresh gesture at its top.
      setDirection('up');
    };
  }, [element, topThreshold, minDelta]);

  return direction;
}
